import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import { GoogleAnalyticsHelper } from '@rightship-tech/common-ui/helpers/google-analytics.helper';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { PLATFORM, autoinject, computedFrom } from "aurelia-framework";
import { RouteConfig, Router, RouterConfiguration, NavigationInstruction } from "aurelia-router";
import { RouteGenerator } from "@rightship-tech/common-ui/route-generator";
import { Messages } from "@rightship-tech/common-ui/constants/event-messages.constants";
import { UserClientService } from '@rightship-tech/common-ui/services/basic/user-client.service';
import { RsInspectionRouteConstants } from '../../../constants/route.constants';
import { CacheClientService, CacheLocation } from '@rightship-tech/common-ui/services/basic/client-caching.service';
import { RightshipInspectionService } from '../../../services/rightship-inspection.service';
import { GetRightShipQiUserResponseUser } from '@rightship-tech/common-ui/models/user.models';

@autoinject
export class Layout {

    private _subscriptions: Subscription[] = [];
    public currentUser: GetRightShipQiUserResponseUser;
    public currentRoute: RouteConfig;
    public showDatePicker: boolean = false;
    public documentClick: EventListenerOrEventListenerObject;
    public siteName: string;
    public email: string;

    constructor(private routeGenerator: RouteGenerator, public userClientService: UserClientService, private router: Router,
        private rightshipInspectionService: RightshipInspectionService,
        private cacheClientService: CacheClientService, private eventAggregator: EventAggregator, private googleAnalyticsService: GoogleAnalyticsService) {

            this.documentClick = e => {
                if (!(<any>e).propagationStopped) {
                    eventAggregator.publish(Messages.documentClick, { source: "document" });
                }
            };

        //Subscribe to show date picker event
        this._subscriptions.push(eventAggregator.subscribe(Messages.showDatePicker, () => {
            this.showDatePicker = true;
        }));

        //Subscribe to hide date picker event
        this._subscriptions.push(eventAggregator.subscribe(Messages.hideDatePicker, () => {
            this.showDatePicker = false;
        }));

        //Subscribe all events for Google Analytics tracking
        GoogleAnalyticsHelper.subscribeEvents(this._subscriptions, this.googleAnalyticsService, this.eventAggregator);
    }

    @computedFrom("email")
    get userEmail() {
        return this.email;
    }

    @computedFrom("siteName")
    get getSiteName() {
        return this.siteName;
    }

    async attached () {

        //Get current user
        if (this.userClientService.currentUser && this.userClientService.currentUser.user) {
            this.currentUser = this.userClientService.currentUser.user;

            //Set user email in local storage
            await this.cacheClientService.put('user-email', this.currentUser.email, CacheLocation.LocalStorage);
        } else {
            const currentInstruction: NavigationInstruction = this.router.currentInstruction;
            if (currentInstruction && currentInstruction.params && currentInstruction.params.encryptedData) {
                const userInfo = await this.rightshipInspectionService.getGetCurrentUserInfo(currentInstruction.params.encryptedData);
                
                //Set user email in local storage
                await this.cacheClientService.put('user-email', userInfo.inspectorEmail, CacheLocation.LocalStorage);
            }
        }

        //Get site name and email from cache
        this.siteName = await this.cacheClientService.get('site-name', CacheLocation.LocalStorage, false);
        this.email = await this.cacheClientService.get('user-email', CacheLocation.LocalStorage, false);

        //Document click event
        document.addEventListener("click", this.documentClick);
    }

    detached() {
        
        if (document) {
            document.removeEventListener("click", this.documentClick);
        }
        //Dispose of all subscriptions
        this._subscriptions.forEach(x => {
            x.dispose();
        });
        this._subscriptions = [];

        //Remove site name and email from cache
        if(this.siteName) this.cacheClientService.remove('site-name', CacheLocation.LocalStorage);
        if(this.email) this.cacheClientService.remove('user-email', CacheLocation.LocalStorage);
    }

    //Get user initials
    public getUserInitials() {
        let initials = "";
        if (this.email) {
            initials = this.email.charAt(0);
        }
        return initials.toUpperCase();
    }

    //Configure the router
    public configureRouter(config: RouterConfiguration, router: Router) {
        config.title = "RightShip Inspection";
        config.options.pushState = true;
        config.options.hashChange = true;
        config.options.root = "/";
        
        //Add RS Inspection app specific routes
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../inspection/inspection"), name: RsInspectionRouteConstants.Inspection, route: "/inspection/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../inspection-documents/inspection-documents"), name: RsInspectionRouteConstants.InspectionDocuments, route: "/inspection-documents/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../attachment-viewer/attachment-viewer"), name: RsInspectionRouteConstants.AttachmentViewer, route: "/attachment-viewer" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../inspection-report/inspection-report"), name: RsInspectionRouteConstants.InspectionReport, route: "/inspection-report" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../inspection-report-unavailable/inspection-report-unavailable"), name: RsInspectionRouteConstants.InspectionReportUnavailable, route: "/inspection-report-unavailable" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../inspection-closed/inspection-closed"), name: RsInspectionRouteConstants.InspectionClosed, route: "/inspection-closed" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../withdraw-inspection/withdraw-inspection"), name: RsInspectionRouteConstants.WithdrawInspection, route: "/inspection/withdraw/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../change-inspection-date/change-inspection-date"), name: RsInspectionRouteConstants.ChangeInspectionDate, route: "/inspection/change-date/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../cancel-inspection/cancel-inspection"), name: RsInspectionRouteConstants.CancelInspection, route: "/inspection/cancel/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../proposal/proposal"), name: RsInspectionRouteConstants.Proposal, route: "/proposal-inspection/submit/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../invoice/invoice"), name: RsInspectionRouteConstants.Invoice, route: "/invoice-inspection/submit/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../submitted-invoice/submitted-invoice"), name: RsInspectionRouteConstants.SubmittedInvoice, route: "/invoice-inspection/submitted/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../submitted-proposal/submitted-proposal"), name: RsInspectionRouteConstants.SubmittedProposal, route: "/proposal-inspection/submitted/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../submitted-withdrawal/submitted-withdrawal"), name: RsInspectionRouteConstants.SubmittedWithdrawal, route: "/withdrawal-inspection/submitted/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../submitted-cancellation/submitted-cancellation"), name: RsInspectionRouteConstants.SubmittedCancellation, route: "/cancellation-inspection/submitted/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../submitted-change-inspection-date/submitted-change-inspection-date"), name: RsInspectionRouteConstants.SubmittedChangeInspectionDate, route: "/change-inspection-date/submitted/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../close-request/close-request"), name: RsInspectionRouteConstants.CloseRequest, route: "/proposal-inspection/close/:encryptedData" });
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../decline-proposal/decline-proposal"), name: RsInspectionRouteConstants.DeclineProposal, route: "/proposal-inspection/decline/:encryptedData" });
        //Fallback route
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../not-found/not-found"), name: RsInspectionRouteConstants.NotFound, title: "Page not found", route: ["*path", "/"] })

        //Add routes from other apps
        config.map(this.routeGenerator.getRoutes());
        this.routeGenerator.setRouter(router);
    }
}